import type { RoomDates } from '@/providers/IbkProvider';

type Props = {
  venueId: string;
  geoLocation: {
    lat: number;
    lon: number;
  };
  isGuestRoomFilledOut: boolean;
  preFilledDatesValid: boolean;
  searchedFromModal?: boolean;
  preFilledStartDate: Date | undefined;
  preFilledEndDate: Date | undefined;
  guestRoomDates: RoomDates;
  meetingRoomDates: RoomDates;
  guestRoomCountFromWidgetModal: number;
  guestRoomCount: number | undefined;
  isMeetingRoomFilledOut: boolean;
  meetingRoomAttendeesFromWidgetModel: number | undefined;
  meetingRoomCount: number | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format: any;
};

type Count = {
  min?: number;
  max?: number;
};

type RoomCriteria =
  | {
      and: {
        adultCount?: Count;
        roomInventory?: Count;
        maxAttendees?: Count;
      }[];
    }
  | undefined;

type RoomAvailability = {
  startDate?: string;
  endDate?: string;
  roomCriteria: RoomCriteria;
};

export type RoomSelections = {
  withSleepingRoomAvailability?: RoomAvailability;
  withMeetingRoomAvailability?: RoomAvailability;
};
type VenueSearchSelections = {
  filter: {
    and: {
      type: string;
      venueId: string[];
    }[];
    type: string;
  };
  sort: {
    geoContext: {
      centerPoint: {
        lat: number;
        lon: number;
      };
    };
    order: {
      type: string;
      direction: string;
    }[];
  };
  pagination: {
    from: number;
    size: number;
  };
  selections: RoomSelections;
};
export function getPayloadForFetchingIbkAvailability({
  venueId,
  geoLocation,
  isGuestRoomFilledOut,
  preFilledDatesValid,
  searchedFromModal,
  preFilledStartDate,
  preFilledEndDate,
  guestRoomDates,
  meetingRoomDates,
  guestRoomCountFromWidgetModal,
  guestRoomCount,
  isMeetingRoomFilledOut,
  meetingRoomAttendeesFromWidgetModel,
  meetingRoomCount,
  format
}: Props): VenueSearchSelections {
  const ibkPayload: VenueSearchSelections = {
    filter: {
      and: [
        {
          type: 'VENUE_ID',
          venueId: [venueId]
        }
      ],
      type: 'AND'
    },
    sort: {
      geoContext: {
        centerPoint: geoLocation
      },
      order: [
        {
          type: 'ALPHABETICAL',
          direction: 'ASC'
        }
      ]
    },
    pagination: {
      from: 0,
      size: 4
    },
    selections: {
      withSleepingRoomAvailability: undefined,
      withMeetingRoomAvailability: undefined
    }
  };

  // Adding rooms to query:
  // isGuestRoomFilledOut represents filters in the modal
  // preFilledDatesValid represents dates being filled on previous page
  // If modal is open, we ensure we don't use the preFilled values anymore

  // Add guestRoom to query
  if (isGuestRoomFilledOut || (preFilledDatesValid && !searchedFromModal)) {
    ibkPayload.selections.withSleepingRoomAvailability = {
      startDate: format(guestRoomDates?.startDate, 'yyyy-MM-dd') || format(preFilledStartDate, 'yyyy-MM-dd'),
      endDate: format(guestRoomDates?.endDate, 'yyyy-MM-dd') || format(preFilledEndDate, 'yyyy-MM-dd'),
      roomCriteria: {
        and: [
          // Default occupancy per guest room to 1, since we don't expose occupancy filter on UI, yet
          { adultCount: { min: 1 } },
          {
            roomInventory: {
              min: guestRoomCountFromWidgetModal || guestRoomCount
            }
          }
        ]
      }
    };
  } else {
    ibkPayload.selections.withSleepingRoomAvailability = undefined;
  }

  // Add meetingRoom to query
  if (isMeetingRoomFilledOut || (preFilledDatesValid && !searchedFromModal)) {
    ibkPayload.selections.withMeetingRoomAvailability = {
      startDate: format(meetingRoomDates?.startDate, 'yyyy-MM-dd') || format(preFilledStartDate, 'yyyy-MM-dd'),
      endDate: format(meetingRoomDates?.endDate, 'yyyy-MM-dd') || format(preFilledEndDate, 'yyyy-MM-dd'),
      roomCriteria: {
        and: [
          {
            maxAttendees: {
              min: meetingRoomAttendeesFromWidgetModel || meetingRoomCount
            }
          }
        ]
      }
    };
  } else {
    ibkPayload.selections.withMeetingRoomAvailability = undefined;
  }
  return ibkPayload;
}
